import React, { useCallback, useEffect, useState } from "react";
import {
  VStack, HStack, Text, Badge, Button, Spinner, Card, CardHeader, CardBody,
  Menu, MenuButton, MenuList, MenuItem, useDisclosure, useToast, Input, Select,
} from "@chakra-ui/react";
import { AddIcon, ChevronDownIcon } from "@chakra-ui/icons";
import {
  fetchOrdersForAudio,
  assignOrderItem,
  unassignOrderItem,
  transitionOrderItem,
  createOrderItem,
} from "../../services/clinicComplianceService";
import AssigneePickerModal from "./AssigneePickerModal";
import { normalizeStatus, STATUS_META, ACTION_LABELS, DECISION_ACTIONS, availableActions } from "../../utils/workItemStatus";
import { onWorkItemsChanged, emitWorkItemsChanged } from "../../utils/workItemsBus";
import { useUserInfo } from "../../hooks/medstudent/useUserInfo";

// Per-recording Orders & Followups, shown in the audio copilot A&P tab.
export default function AudioActionItemsPanel({ schema, ehrId, audioNumber, patientName }) {
  const toast = useToast();
  const { userId } = useUserInfo();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [assignTarget, setAssignTarget] = useState(null);
  const assignDisclosure = useDisclosure();
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ item_type: "order", description: "" });

  const load = useCallback(async () => {
    if (!ehrId) { setLoading(false); return; }
    setLoading(true);
    try {
      const data = await fetchOrdersForAudio(ehrId, audioNumber, { schema });
      const flat = (data.patients || []).flatMap((p) => p.items || []);
      setItems(flat);
    } catch (err) {
      toast({ title: "Could not load action items", description: err.message, status: "warning", duration: 3000, isClosable: true });
    } finally {
      setLoading(false);
    }
  }, [ehrId, audioNumber, schema, toast]);

  useEffect(() => { load(); }, [load]);
  // Another surface (the Orders modal, the Compliance tab) may have assigned
  // or transitioned one of these items — reload so this panel doesn't show
  // stale status.
  // Internal tasks never appear here, so a task change needs no refetch.
  useEffect(
    () => onWorkItemsChanged((e) => {
      if (e?.detail?.kind === "task") return;
      load();
    }),
    [load],
  );

  const withBusy = async (id, fn) => {
    setBusyId(id);
    try {
      await fn();
      emitWorkItemsChanged({ kind: "order" });
      await load();
    }
    catch (err) { toast({ title: "Action failed", description: err.message, status: "error", duration: 4000, isClosable: true }); }
    finally { setBusyId(null); }
  };

  const handleAssign = (user) => {
    if (!assignTarget) return;
    const target = assignTarget;
    setAssignTarget(null);
    withBusy(target.id, () => assignOrderItem(target.id, user.id, { assignedBy: userId, schema }));
  };

  const handleAdd = async () => {
    if (!draft.description.trim()) return;
    setAdding(false);
    try {
      await createOrderItem(
        { patient_name: patientName || "", ehr_id: ehrId, item_type: draft.item_type, description: draft.description.trim(), audio_number: audioNumber },
        { schema },
      );
      setDraft({ item_type: "order", description: "" });
      await load();
    } catch (err) {
      toast({ title: "Could not add", description: err.message, status: "error", duration: 4000, isClosable: true });
    }
  };

  const renderGroup = (label, type) => {
    const rows = items.filter((it) => it.item_type === type);
    return (
      <Card variant="outline" size="sm" boxShadow="sm" bg="white">
        <CardHeader py={2} pb={1}>
          <Text fontSize="xs" fontWeight="semibold" color="gray.700" textTransform="uppercase" letterSpacing="wide">
            {label}
          </Text>
        </CardHeader>
        <CardBody pt={0}>
          {rows.length === 0 ? (
            <Text fontSize="sm" color="gray.500" fontStyle="italic">None.</Text>
          ) : (
            <VStack align="stretch" spacing={2}>
              {rows.map((item) => {
                const normalized = normalizeStatus(item.status);
                const meta = STATUS_META[normalized] || STATUS_META.unassigned;
                const itemShape = {
                  status: normalized,
                  assignedTo: item.assigned_to_user_id,
                  assignedBy: item.assigned_by_user_id,
                };
                // Reassign / unassign are this panel's own menu entries; the
                // reject / reassign / cancel windows live in Messages → Tasks
                // and Orders & followups, so they aren't offered here.
                const actions = availableActions({ ...itemShape, kind: "order" }, userId)
                  .filter((action) => !DECISION_ACTIONS.includes(action));
                return (
                  <HStack key={item.id} justify="space-between" flexWrap="wrap" gap={2}>
                    <HStack flex="1" minW="200px" spacing={2}>
                      <Badge colorScheme={meta.colorScheme} variant="solid" borderRadius="md" px={2}>{meta.label}</Badge>
                      <Text fontSize="sm" color="gray.800">{item.description}</Text>
                    </HStack>
                    <HStack spacing={2}>
                      {item.assigned_to && <Text fontSize="xs" color="gray.600">→ {item.assigned_to.name || item.assigned_to.email}</Text>}
                      <Menu>
                        <MenuButton as={Button} size="xs" variant="outline" rightIcon={<ChevronDownIcon />} isLoading={busyId === item.id}>Actions</MenuButton>
                        <MenuList>
                          <MenuItem onClick={() => { setAssignTarget(item); assignDisclosure.onOpen(); }}>{item.assigned_to ? "Reassign…" : "Assign…"}</MenuItem>
                          {item.assigned_to && <MenuItem onClick={() => withBusy(item.id, () => unassignOrderItem(item.id, { schema }))}>Unassign</MenuItem>}
                          {actions.map((action) => (
                            <MenuItem
                              key={action}
                              onClick={() => withBusy(item.id, () => transitionOrderItem(item.id, action, { actorId: userId, schema }))}
                            >
                              {ACTION_LABELS[action]}
                            </MenuItem>
                          ))}
                          {!item.assigned_to && actions.length === 0 && (
                            <MenuItem isDisabled>Assign before updating status</MenuItem>
                          )}
                        </MenuList>
                      </Menu>
                    </HStack>
                  </HStack>
                );
              })}
            </VStack>
          )}
        </CardBody>
      </Card>
    );
  };

  if (loading) {
    return <HStack py={3}><Spinner size="sm" /><Text fontSize="sm" color="gray.500">Loading orders & followups…</Text></HStack>;
  }
  if (!ehrId) {
    return null;
  }

  return (
    <VStack align="stretch" spacing={3}>
      {renderGroup("Orders", "order")}
      {renderGroup("Followups", "followup")}

      {adding ? (
        <HStack>
          <Select size="sm" maxW="130px" value={draft.item_type} onChange={(e) => setDraft({ ...draft, item_type: e.target.value })}>
            <option value="order">Order</option>
            <option value="followup">Followup</option>
          </Select>
          <Input size="sm" placeholder="Description…" value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} onKeyDown={(e) => { if (e.key === "Enter") handleAdd(); }} autoFocus />
          <Button size="sm" colorScheme="purple" onClick={handleAdd}>Add</Button>
          <Button size="sm" variant="ghost" onClick={() => setAdding(false)}>Cancel</Button>
        </HStack>
      ) : (
        <Button size="sm" leftIcon={<AddIcon />} variant="outline" alignSelf="flex-start" onClick={() => setAdding(true)}>
          Add order / followup
        </Button>
      )}

      <AssigneePickerModal
        isOpen={assignDisclosure.isOpen}
        onClose={() => { assignDisclosure.onClose(); setAssignTarget(null); }}
        onSelect={handleAssign}
        title={assignTarget ? `Assign: ${assignTarget.description}` : "Assign to user"}
        schema={schema}
      />
    </VStack>
  );
}
